/**
 * RestoID — the Continue-flow identifier form.
 * ---------------------------------------------------------------------------
 * A RestoID-shaped stand-in for the upstream `src/pages/Continue/IdentifierProfileForm`,
 * used by the three "link an identifier" steps (`/continue/email`, `/continue/phone`,
 * `/continue/emailOrPhone`) and by `/continue/username`.
 *
 * Upstream renders a `SmartInputField`: a 44px control with a floating label notched
 * into a fieldset border, an animated country-code prefix and a clear button. RestoID's
 * `Field` (label ABOVE, own fill, radius 13, lime focus ring, `SIZE.field` tall) and
 * `PhoneInput` (+7 mask) are structurally different controls, and the theme overlay can
 * only repaint colors, never restructure — so the PRESENTATION is rebuilt here while the
 * FORM LOGIC stays equivalent to upstream, line for line:
 *
 *   - the same react-hook-form setup (`reValidateMode: 'onBlur'`, the same
 *     `{ identifier: { type, value } }` default) and the same `validate` rule, so the
 *     same messages: `getGeneralIdentifierErrorMessage(enabledTypes, 'required')` on an
 *     empty value, and `validateIdentifierField(type, value, usernamePolicy)` mapped
 *     through `t('error.<code>', data)` otherwise — including the username policy read
 *     off `PageContext.experienceSettings`;
 *   - the same `clearErrorMessage()` on every submit AND whenever the form turns invalid
 *     (upstream's `useEffect` on `isValid`);
 *   - the same submit guard: a value whose identifier TYPE is still undetermined is not
 *     submitted at all (`if (!type) return`);
 *   - the same danger rule (`!!errors.identifier || !!errorMessage`), the field error and
 *     the form-level error rendered as two separate blocks, in upstream's order;
 *   - the same `action.continue` CTA off `isSubmitting`, and the hidden submit input that
 *     makes Enter submit a single-field form.
 *
 * Deliberate differences, all forced by the control swap:
 *   - PHONE gets RestoID's `PhoneInput` instead of the country-code dropdown. That fixes
 *     the calling code to +7, exactly as the sign-in and forgot-password screens already
 *     do; the value handed to the form is still `${countryCode}${nationalNumber}`
 *     (`phoneToIdentifierValue`), which is what `validatePhone` and the Experience API
 *     expect. An empty mask submits `''`, so the "required" branch still fires.
 *   - EMAIL-OR-PHONE gains an explicit switch, but KEEPS upstream's type sniffing: the
 *     text control still detects with the full `enabledTypes` set, and the moment
 *     `detectIdentifierType` calls the value a phone number (3+ digits) it hands the
 *     digits to `PhoneInput` and switches — the closest RestoID can come to upstream's
 *     single control morphing into a phone input mid-typing. Without that hand-over the
 *     text control could only ever produce an Email value, so a phone number typed into
 *     it would be rejected as an invalid email and could never be submitted.
 *   - the clear ("x") suffix goes with `SmartInputField`; RestoID fields have no clear
 *     affordance. `autoFocus` only applies to the text control — `PhoneInput` takes no
 *     such prop.
 */
import { SignInIdentifier } from '@logto/schemas';
import { type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import type {
  IdentifierInputType,
  IdentifierInputValue,
} from '@/shared/components/InputFields/SmartInputField';
import { detectIdentifierType } from '@/shared/components/InputFields/SmartInputField/utils';
import {
  getGeneralIdentifierErrorMessage,
  identifierInputPlaceholderMap,
  validateIdentifierField,
} from '@/utils/form';

import { useCopy } from '../copy';

import {
  Button,
  C,
  FONT,
  Field,
  LinkText,
  PhoneInput,
  Stack,
  formatPhoneNat,
  phoneNsn,
  phoneToIdentifierValue,
} from './restoid';

type Props = {
  autoFocus?: boolean;
  defaultType?: IdentifierInputType;
  enabledTypes: IdentifierInputType[];
  onSubmit?: (identifier: IdentifierInputType, value: string) => Promise<void> | void;
  errorMessage?: string;
  clearErrorMessage?: () => void;
};

type FormState = {
  identifier: IdentifierInputValue;
};

/** The label above a control, in the same type as `Field`'s own label. */
const ControlLabel = ({ children }: { children: ReactNode }) => (
  <label style={{ display: 'block', color: C.sub, font: `500 13px/1 ${FONT}`, marginBottom: 9 }}>
    {children}
  </label>
);

/**
 * The form-level error (send-code failure, "identifier already in use") — upstream's
 * `<ErrorMessage>` between the input and the CTA, in RestoID's error type. `role="alert"`
 * is kept so a screen reader announces it as soon as the submit comes back.
 */
const FormError = ({ children }: { children: ReactNode }) => (
  <div role="alert" style={{ color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}>
    {children}
  </div>
);

const ContinueIdentifierForm = ({
  autoFocus,
  defaultType,
  enabledTypes,
  onSubmit,
  errorMessage,
  clearErrorMessage,
}: Props) => {
  const { t } = useTranslation();
  const c = useCopy();
  const { experienceSettings } = useContext(PageContext);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: {
      identifier: {
        type: defaultType,
        value: '',
      },
    },
  });

  const hasPhone = enabledTypes.includes(SignInIdentifier.Phone);
  // Everything that is NOT a phone is what the text control is LABELLED with; the phone
  // side of `enabledTypes` has its own masked widget, so it never appears in the label.
  const textTypes = enabledTypes.filter((type) => type !== SignInIdentifier.Phone);
  /**
   * Upstream pins the control to `type="email"` only when email is the ONLY enabled
   * identifier (`getInputHtmlProps` checks `enabledTypes.length === 1`); as soon as a
   * phone is also allowed it stays `type="text"`. RestoID must follow, because digits
   * have to be typable here to reach the phone hand-over below — and because the
   * browser's own constraint validation on a `type="email"` input would otherwise
   * swallow the submit before react-hook-form ever ran the `validate` rule.
   */
  const isEmailOnly = enabledTypes.length === 1 && enabledTypes[0] === SignInIdentifier.Email;

  const [isPhoneMode, setIsPhoneMode] = useState(
    hasPhone && (textTypes.length === 0 || defaultType === SignInIdentifier.Phone)
  );
  // The +7 mask as the user sees it; the form holds the API-shaped value instead.
  const [phone, setPhone] = useState('');
  const phoneWrapperRef = useRef<HTMLDivElement>(null);
  // Raised only by the text control's hand-over, so the caret follows the digits into the
  // mask. A mount in phone mode, or a deliberate click on the switch link, must not steal
  // focus — upstream focused on mount through `autoFocus`, which `PhoneInput` has no slot for.
  const isHandingOverRef = useRef(false);

  // Upstream sniffs the type against the whole of `enabledTypes`, phone included
  // (`SmartInputField` forwards `enabledTypes` straight to `detectIdentifierType`).
  const detectTextType = useCallback(
    (value: string, currentType?: IdentifierInputType) =>
      detectIdentifierType({ value, enabledTypeSet: new Set(enabledTypes), currentType }),
    [enabledTypes]
  );

  useEffect(() => {
    if (!isPhoneMode || !isHandingOverRef.current) {
      return;
    }

    isHandingOverRef.current = false;
    const input = phoneWrapperRef.current?.querySelector('input');
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  }, [isPhoneMode]);

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async ({ identifier: { type, value } }) => {
        if (!type) {
          return;
        }

        await onSubmit?.(type, value);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  const fieldError = errors.identifier?.message;
  const isDanger = Boolean(errors.identifier) || Boolean(errorMessage);
  const canSwitchMode = hasPhone && textTypes.length > 0;

  return (
    <form onSubmit={onSubmitHandler}>
      <Stack gap={18}>
        <Controller
          control={control}
          name="identifier"
          rules={{
            validate: (identifier) => {
              const { type, value } = identifier;

              if (!type || !value) {
                return getGeneralIdentifierErrorMessage(enabledTypes, 'required');
              }

              const errorMessage = validateIdentifierField(
                type,
                value,
                experienceSettings?.usernamePolicy
              );

              if (errorMessage) {
                return typeof errorMessage === 'string'
                  ? t(`error.${errorMessage}`)
                  : t(`error.${errorMessage.code}`, errorMessage.data ?? {});
              }

              return true;
            },
          }}
          render={({ field }) => {
            /**
             * Upstream's one control MORPHED into a phone input the moment the value
             * sniffed as a phone number; RestoID's `PhoneInput` is a separate masked
             * widget, so the equivalent is to move the digits into it and switch. The
             * mask is built with `formatPhoneNat`, so the user lands on exactly the value
             * they would have got by typing those digits into the phone control directly.
             */
            const handOverToPhone = (digits: string) => {
              const mask = formatPhoneNat(digits);
              isHandingOverRef.current = true;
              setPhone(mask);
              setIsPhoneMode(true);
              field.onChange({
                type: SignInIdentifier.Phone,
                value: phoneNsn(mask) ? phoneToIdentifierValue(mask) : '',
              });
            };

            return (
              <Stack gap={14}>
                {isPhoneMode ? (
                  // `reValidateMode: 'onBlur'` only re-runs the rules when RHF's own
                  // onBlur fires. `PhoneInput` exposes no onBlur prop, so we catch the
                  // inner input's blur as it bubbles through this wrapper — the same
                  // moment upstream's SmartInputField reported it.
                  <div ref={phoneWrapperRef} onBlur={field.onBlur}>
                    <ControlLabel>{c('phoneLabel')}</ControlLabel>
                    <PhoneInput
                      value={phone}
                      danger={isDanger}
                      onChange={(next) => {
                        setPhone(next);
                        field.onChange({
                          type: SignInIdentifier.Phone,
                          // An empty mask must stay empty, or the "required" branch of the
                          // validate rule would never fire (a bare "7" is not empty).
                          value: phoneNsn(next) ? phoneToIdentifierValue(next) : '',
                        });
                      }}
                    />
                    {fieldError ? (
                      <div
                        role="alert"
                        style={{ marginTop: 8, color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}
                      >
                        {fieldError}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <Field
                    autoFocus={autoFocus}
                    label={textTypes
                      .map((type) => t(identifierInputPlaceholderMap[type]))
                      .join(' / ')}
                    inputType={isEmailOnly ? 'email' : 'text'}
                    inputMode={isEmailOnly ? 'email' : 'text'}
                    autoComplete={textTypes.join(' ')}
                    value={field.value.value}
                    danger={isDanger}
                    errorMessage={fieldError}
                    onChange={(next) => {
                      // Upstream's SmartInputField trimmed on every change
                      // (`use-smart-input-field` → `setInputValue(value.trim())`), and
                      // `emailRegEx` is /^\S+@\S+\.\S+$/ — so without this a pasted
                      // " a@corp.com " would be rejected where upstream accepted it.
                      const value = next.trim();
                      const type = detectTextType(value, field.value.type);

                      if (hasPhone && type === SignInIdentifier.Phone) {
                        handOverToPhone(value);
                        return;
                      }

                      field.onChange({ type, value });
                    }}
                    onBlur={field.onBlur}
                  />
                )}
                {canSwitchMode ? (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <LinkText
                      onClick={() => {
                        const nextIsPhone = !isPhoneMode;
                        setIsPhoneMode(nextIsPhone);
                        setPhone('');
                        clearErrorMessage?.();
                        field.onChange({
                          type: nextIsPhone ? SignInIdentifier.Phone : detectTextType(''),
                          value: '',
                        });
                      }}
                    >
                      {isPhoneMode ? c('useEmailInstead') : c('usePhoneInstead')}
                    </LinkText>
                  </div>
                ) : null}
              </Stack>
            );
          }}
        />

        {errorMessage ? <FormError>{errorMessage}</FormError> : null}

        <Button title={t('action.continue')} htmlType="submit" isLoading={isSubmitting} />
      </Stack>
      <input hidden type="submit" />
    </form>
  );
};

export default ContinueIdentifierForm;
